import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Box, Button, Flex, Icon, Spinner, Text } from "@chakra-ui/react";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckIcon,
  PencilSquareIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { BASE_API_URL } from "../../Components/config";
import { useClassroomColorMode, c } from "../classroomColors";
import { useClassroomToast } from "../classroomToast";
import ReportSheet from "./ReportSheet";
import ReportSummary from "./ReportSummary";
import ReportQuestionCard from "./ReportQuestionCard";
import { s } from "./reportTokens";
import "../classroom-theme.css";
import "./report-print.css";

/**
 * The generated report for one submitted assignment (Figma 95:38, 96:128).
 *
 * Two A4 sheets: the summary, then the per-question breakdown. The breakdown
 * runs past one sheet on a long paper, so the print stylesheet flows it onto
 * further pages and keeps each question card whole rather than clipping to a
 * fixed height.
 *
 * Generation is a teacher action and costs money, so this page never triggers
 * it on load. It reads the stored report; if there is none, a teacher is
 * offered the button. The server is the authority on who may do either.
 */
const AssignmentReport = () => {
  const { attemptGuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useClassroomColorMode();
  const toast = useClassroomToast(isDark);

  // Set when we arrive from the teacher's review view. Absent on a direct link,
  // in which case we still offer the button and let the server decide.
  const isInstructor = location.state?.viewerRole !== "STUDENT";

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  // The instructor's corrections, held apart from the report until saved so
  // Cancel is a real cancel.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const authHeader = {
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  };

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/api/v1/user-paper-attempts/report/${attemptGuid}`,
        { headers: authHeader }
      );
      setReport(data);
      setError(null);
    } catch (err) {
      setReport(null);
      setError(
        err?.response?.status === 404
          ? null // no report yet: an offer, not an error
          : err?.response?.data?.detail || "This report could not be loaded."
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptGuid]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const startEditing = () => {
    setDraft({
      strengths: [...(report.strengths || [])],
      gaps: [...(report.gaps || [])],
      questions: Object.fromEntries(
        (report.questions || []).map((question) => [
          question.question_guid,
          {
            why: question.why || "",
            model_answer_text: question.model_answer_text || "",
          },
        ])
      ),
    });
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft(null);
  };

  const saveEdits = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BASE_API_URL}/api/v1/user-paper-attempts/report/${attemptGuid}`,
        {
          strengths: (draft.strengths || []).map((t) => t.trim()).filter(Boolean),
          gaps: (draft.gaps || []).map((t) => t.trim()).filter(Boolean),
          questions: Object.entries(draft.questions || {}).map(([guid, value]) => ({
            question_guid: guid,
            why: value.why || "",
            model_answer_text: value.model_answer_text || "",
          })),
        },
        { headers: authHeader }
      );
      setReport(data);
      setEditing(false);
      setDraft(null);
      toast({ title: "Report saved", status: "success" });
    } catch (err) {
      toast({
        title: "Could not save your changes",
        description: err?.response?.data?.detail || "Please try again.",
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${BASE_API_URL}/api/v1/user-paper-attempts/report/${attemptGuid}`,
        {},
        { headers: authHeader }
      );
      setReport(data);
      setError(null);
      toast({
        title: "Report ready",
        description: "The student will see it once you release feedback.",
        status: "success",
      });
    } catch (err) {
      toast({
        title: "Could not generate the report",
        description: err?.response?.data?.detail || "Please try again.",
        status: "error",
      });
    } finally {
      setGenerating(false);
    }
  };

  const chromeButton = {
    height: "36px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "400",
  };

  return (
    <Box
      className="stande-classroom report-root"
      data-theme={isDark ? "dark" : "light"}
      bg={c.page}
      minHeight="100vh"
      paddingBottom="60px"
    >
      <Flex
        className="report-chrome"
        align="center"
        justify="space-between"
        gap="12px"
        padding="16px 24px"
        position="sticky"
        top="0"
        zIndex="10"
        bg={c.surface}
        borderBottom="1px solid"
        borderColor={c.border}
      >
        <Button
          {...chromeButton}
          variant="ghost"
          color={c.textSub}
          _hover={{ bg: c.hover }}
          leftIcon={<Icon as={ArrowLeftIcon} w="16px" h="16px" />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>

        <Flex gap="10px" align="center">
          {editing && (
            <Text fontSize="12px" color={c.textMuted} display={{ base: "none", md: "block" }}>
              Regenerating replaces your edits
            </Text>
          )}

          {report && isInstructor && !editing && (
            <>
              <Button
                {...chromeButton}
                variant="outline"
                borderColor={c.border}
                color={c.textSub}
                _hover={{ bg: c.hover }}
                isLoading={generating}
                loadingText="Regenerating"
                leftIcon={<Icon as={ArrowPathIcon} w="16px" h="16px" />}
                onClick={handleGenerate}
              >
                Regenerate
              </Button>
              <Button
                {...chromeButton}
                variant="outline"
                borderColor={c.border}
                color={c.textSub}
                _hover={{ bg: c.hover }}
                leftIcon={<Icon as={PencilSquareIcon} w="16px" h="16px" />}
                onClick={startEditing}
              >
                Edit
              </Button>
            </>
          )}

          {editing && (
            <>
              <Button
                {...chromeButton}
                variant="outline"
                borderColor={c.border}
                color={c.textSub}
                _hover={{ bg: c.hover }}
                leftIcon={<Icon as={XMarkIcon} w="16px" h="16px" />}
                onClick={cancelEditing}
              >
                Cancel
              </Button>
              <Button
                {...chromeButton}
                bg={c.solidBg}
                color={c.solidText}
                _hover={{ bg: c.solidBg, opacity: 0.86 }}
                isLoading={saving}
                loadingText="Saving"
                leftIcon={<Icon as={CheckIcon} w="16px" h="16px" />}
                onClick={saveEdits}
              >
                Save
              </Button>
            </>
          )}

          {report && !editing && (
            <Button
              {...chromeButton}
              bg={c.solidBg}
              color={c.solidText}
              _hover={{ bg: c.solidBg, opacity: 0.86 }}
              leftIcon={<Icon as={ArrowDownTrayIcon} w="16px" h="16px" />}
              onClick={() => window.print()}
            >
              Download PDF
            </Button>
          )}
        </Flex>
      </Flex>

      {/* Print only: a fixed element is the one thing Chrome repeats on every
          printed page, so this is what carries the page tint to the paper. */}
      <Box className="report-page-tint" display="none" />

      <Flex className="report-stack" direction="column" align="center" gap="24px" paddingTop="28px">
        {loading && <Spinner color={c.accent} size="lg" marginTop="80px" />}

        {!loading && error && (
          <Text color={c.textSub} fontSize="14px" marginTop="80px">
            {error}
          </Text>
        )}

        {!loading && !error && !report && (
          <Flex
            direction="column"
            align="center"
            gap="14px"
            marginTop="80px"
            maxWidth="420px"
            textAlign="center"
          >
            <Text color={c.text} fontSize="18px" fontWeight="500">
              No report yet
            </Text>
            <Text color={c.textSub} fontSize="14px" fontWeight="300" lineHeight="1.5">
              Generating a report reads the marking that is already on this
              paper and writes the summary. It is not created automatically, so
              you only pay for the ones you want.
            </Text>
            <Button
              {...chromeButton}
              bg={c.solidBg}
              color={c.solidText}
              _hover={{ bg: c.solidBg, opacity: 0.86 }}
              isLoading={generating}
              loadingText="Generating"
              leftIcon={<Icon as={SparklesIcon} w="16px" h="16px" />}
              onClick={handleGenerate}
            >
              Generate report
            </Button>
          </Flex>
        )}

        {!loading && report && (
          <>
            <ReportSheet>
              <ReportSummary
                report={report}
                editing={editing}
                draft={draft}
                onDraft={setDraft}
              />
            </ReportSheet>

            <Box className="report-page-break">
              <ReportSheet>
                {/* paddingTop, not marginTop: a plain margin here collapses with the
                    first card's own top margin and lands the pill 19px high. */}
                <Box paddingTop={s(24)}>
                  {(report.questions || []).map((question) => (
                    <ReportQuestionCard
                      key={question.question_guid}
                      question={question}
                      editing={editing}
                      draft={draft?.questions?.[question.question_guid]}
                      onDraft={(next) =>
                        setDraft((current) => ({
                          ...current,
                          questions: {
                            ...current.questions,
                            [question.question_guid]: next,
                          },
                        }))
                      }
                    />
                  ))}
                </Box>
              </ReportSheet>
            </Box>
          </>
        )}
      </Flex>
    </Box>
  );
};

export default AssignmentReport;
